import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TablePagination } from "@mui/material";
import { Modal } from "react-bootstrap";
import authService from "../../../utils/authService";
import PlacementStudentsTable, { PlacementTableStudent } from "../../../components/PlacementStudentsTable";

interface Course {
  course_id: string;
  course_name: string;
}

interface Batch {
  batch_id: string;
  batch_name: string;
  del_row?: boolean;
}

type StatusMap = Record<string, boolean>;

const StudentPlacement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState<Course[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [students, setStudents] = useState<PlacementTableStudent[]>([]);
  const [originalStatusMap, setOriginalStatusMap] = useState<StatusMap>({});
  const [statusOverrides, setStatusOverrides] = useState<StatusMap>({});

  const [courseId, setCourseId] = useState("");
  const [batchId, setBatchId] = useState("");

  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [studentsPage, setStudentsPage] = useState(0);
  const [studentsPageSize, setStudentsPageSize] = useState(10);
  const [studentsTotal, setStudentsTotal] = useState(0);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState("");

  const fetchCourses = async () => {
    try {
      setLoadingCourses(true);
      const response = await authService.get("admin/batch/get_all_courses_for_batch/");
      setCourses(response.data?.courses || []);
    } catch (err) {
      console.error("Error fetching courses:", err);
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchBatches = async (nextCourseId: string) => {
    if (!nextCourseId) {
      setBatches([]);
      setBatchId("");
      return;
    }
    try {
      setLoadingBatches(true);
      const response = await authService.get(`admin/batch/get_all_batches/${nextCourseId}/`);
      setBatches((response.data?.batches || []).filter((b: Batch) => !b.del_row));
    } catch (err) {
      console.error("Error fetching batches:", err);
      setBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchStudents = async (
    nextCourseId: string,
    nextBatchId: string,
    pageOverride?: number,
    pageSizeOverride?: number
  ) => {
    if (!nextBatchId) {
      setStudents([]);
      setOriginalStatusMap({});
      setStatusOverrides({});
      setStudentsTotal(0);
      return;
    }
    try {
      setLoadingStudents(true);
      setError("");
      const apiPage = pageOverride ?? studentsPage + 1;
      const apiPageSize = pageSizeOverride ?? studentsPageSize;
      const params = new URLSearchParams({
        course_id: nextCourseId,
        batch_id: nextBatchId,
        page: String(apiPage),
        page_size: String(apiPageSize),
        ignore_placement_boolean: "true",
      });
      const response = await authService.get(
        `placement-officer/students/filter?${params.toString()}`
      );
      const nextStudents: PlacementTableStudent[] = response.data?.students || [];
      setStudents(nextStudents);
      setOriginalStatusMap((prev) => {
        const next = { ...prev };
        nextStudents.forEach((student) => {
          next[student.student_id] = !!(student as any).placement_officer_status;
        });
        return next;
      });
      setStudentsTotal(typeof response.data?.total === "number" ? response.data.total : nextStudents.length);
    } catch (err) {
      console.error("Error fetching students:", err);
      setStudents([]);
      setStudentsTotal(0);
    } finally {
      setLoadingStudents(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const syncCourseBatchToUrl = (nextCourse: string, nextBatch: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextCourse) next.set("course", nextCourse);
        else next.delete("course");
        if (nextBatch) next.set("batch", nextBatch);
        else next.delete("batch");
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (courses.length === 0) return;
    const courseQ = searchParams.get("course");
    if (!courseQ) return;
    const exists = courses.some((c) => c.course_id === courseQ);
    if (exists) {
      setCourseId((prev) => (prev === courseQ ? prev : courseQ));
    } else {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("course");
          next.delete("batch");
          return next;
        },
        { replace: true }
      );
    }
  }, [courses, searchParams, setSearchParams]);

  useEffect(() => {
    if (!courseId || batches.length === 0) return;
    const batchQ = searchParams.get("batch");
    if (!batchQ) return;
    const exists = batches.some((b) => b.batch_id === batchQ);
    if (exists) {
      setBatchId((prev) => (prev === batchQ ? prev : batchQ));
    } else {
      setBatchId("");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("batch");
          return next;
        },
        { replace: true }
      );
    }
  }, [batches, courseId, searchParams, setSearchParams]);

  useEffect(() => {
    fetchBatches(courseId);
    setStudents([]);
    setOriginalStatusMap({});
    setStatusOverrides({});
    setStudentsPage(0);
    setStudentsTotal(0);
  }, [courseId]);

  useEffect(() => {
    if (!batchId) {
      setStudents([]);
      setOriginalStatusMap({});
      setStatusOverrides({});
      setStudentsPage(0);
      setStudentsTotal(0);
      return;
    }
    fetchStudents(courseId, batchId, studentsPage + 1, studentsPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, batchId, studentsPage, studentsPageSize]);

  const getCurrentStatusFor = (studentId: string): boolean => {
    if (studentId in statusOverrides) return statusOverrides[studentId];
    return !!originalStatusMap[studentId];
  };

  const hasChanges = useMemo(() => Object.keys(statusOverrides).length > 0, [statusOverrides]);

  const handleToggleStudent = (studentId: string) => {
    const current = getCurrentStatusFor(studentId);
    const next = !current;
    const original = !!originalStatusMap[studentId];
    setStatusOverrides((prev) => {
      const nextOverrides = { ...prev };
      if (next === original) {
        delete nextOverrides[studentId];
      } else {
        nextOverrides[studentId] = next;
      }
      return nextOverrides;
    });
  };

  const allSelected = useMemo(() => {
    if (students.length === 0) return false;
    return students.every((s) => getCurrentStatusFor(s.student_id));
  }, [students, originalStatusMap, statusOverrides]);

  const handleToggleSelectAll = () => {
    const nextValue = !allSelected;
    setStatusOverrides((prev) => {
      const nextOverrides = { ...prev };
      students.forEach((student) => {
        const original = !!originalStatusMap[student.student_id];
        if (nextValue === original) {
          delete nextOverrides[student.student_id];
        } else {
          nextOverrides[student.student_id] = nextValue;
        }
      });
      return nextOverrides;
    });
  };

  const handleCourseChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setCourseId(value);
    setBatchId("");
    setStudentsPage(0);
    syncCourseBatchToUrl(value, "");
  };

  const handleBatchChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setBatchId(value);
    setStudentsPage(0);
    syncCourseBatchToUrl(courseId, value);
  };

  const handleSave = async () => {
    if (!batchId) {
      setError("Batch filter is mandatory.");
      return;
    }
    if (!hasChanges) {
      setError("No status changes to save.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const changedIds = Object.keys(statusOverrides);
      const selected_ids = changedIds.filter((id) => statusOverrides[id] === true);
      const unselected_ids = changedIds.filter((id) => statusOverrides[id] === false);
      await authService.post("/admin/placement_officer_status/update_batch/", {
        batch_id: batchId,
        selected_ids,
        unselected_ids,
      });
      setOriginalStatusMap((prev) => {
        const next = { ...prev };
        changedIds.forEach((id) => {
          next[id] = statusOverrides[id];
        });
        return next;
      });
      setStatusOverrides({});
      setShowConfirmModal(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save student placement.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      className="border rounded-2 bg-white my-2 me-2 d-flex flex-column"
      sx={{ height: "calc(100vh - 90px)", overflow: "hidden" }}
    >
      <div className="d-flex align-items-center gap-3 p-3 border-bottom">
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Course</InputLabel>
          <Select value={courseId} onChange={handleCourseChange} label="Course" sx={{ height: 36 }}>
            <MenuItem value="">All</MenuItem>
            {courses.map((course) => (
              <MenuItem key={course.course_id} value={course.course_id}>
                {course.course_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel required>Batch</InputLabel>
          <Select
            value={batchId}
            onChange={handleBatchChange}
            label="Batch"
            sx={{ height: 36 }}
            disabled={!courseId || loadingBatches}
          >
            <MenuItem value="">Select Batch</MenuItem>
            {batches.map((batch) => (
              <MenuItem key={batch.batch_id} value={batch.batch_id}>
                {batch.batch_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div className="ms-auto d-flex align-items-center gap-2">
          {error && <span className="text-danger small">{error}</span>}
          <button
            className="btn btn-sm btn-primary"
            disabled={!batchId || !hasChanges || saving}
            onClick={() => setShowConfirmModal(true)}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <PlacementStudentsTable
        students={batchId ? students : []}
        loading={loadingCourses || loadingStudents}
        emptyMessage={batchId ? "No students found for the selected filters" : "Select a batch to view students"}
        selectionMode="checkbox"
        isSelected={(id) => getCurrentStatusFor(id)}
        onToggle={(student) => handleToggleStudent(student.student_id)}
        showSelectAll
        isAllSelected={allSelected}
        onToggleSelectAll={handleToggleSelectAll}
      />

      {batchId && (studentsTotal > 0 || loadingStudents) && (
        <TablePagination
          component="div"
          count={studentsTotal}
          page={studentsPage}
          onPageChange={(_, newPage) => {
            setLoadingStudents(true);
            setStudentsPage(newPage);
          }}
          rowsPerPage={studentsPageSize}
          onRowsPerPageChange={(e) => {
            setLoadingStudents(true);
            setStudentsPageSize(parseInt(e.target.value, 10));
            setStudentsPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            flexShrink: 0,
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#fafafa",
            "& .MuiTablePagination-toolbar": { minHeight: 44, px: 2 },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "12px",
              color: "#666",
              mb: 0,
            },
            "& .MuiTablePagination-actions": { ml: 1 },
            "& .MuiIconButton-root": { p: "6px" },
            "& .Mui-disabled": { opacity: 0.45 },
          }}
        />
      )}

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Save</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to save, this will affect the placement and hirer data?
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-sm btn-secondary" onClick={() => setShowConfirmModal(false)} disabled={saving}>
            No
          </button>
          <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Yes"}
          </button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default StudentPlacement;
